import { createBrowserRouter, Outlet, type RouteObject } from 'react-router';
import { coreOpenRoutes, corePublicRoutes, coreRoutes } from '@saas/module-core/ui';
import { expenseRoutes } from '@saas/module-expense/ui';
import { crmPublicRoutes, crmRoutes } from '@saas/module-crm/ui';
import { surveyRoutes, surveyPublicRoutes } from '@saas/module-survey/ui';
import { contractRoutes, contractPublicRoutes } from '@saas/module-contract/ui';
import { safetyPublicRoutes, safetyRoutes } from '@saas/module-safety/ui';
import { chatRoutes, chatStandaloneRoutes } from '@saas/module-chat/ui';
import { knowledgeRoutes } from '@saas/module-knowledge/ui';
import { relationshipRoutes } from '@saas/module-relationship/ui';
import { companyProfileRoutes } from '@saas/module-company-profile/ui';
import { meetingIngestPublicRoutes, meetingIngestRoutes } from '@saas/module-meeting-ingest/ui';
import { wikiRoutes } from '@saas/module-wiki/ui';
import { storagePublicRoutes, storageRoutes } from '@saas/module-storage/ui';
import { conversationRoutes } from '@saas/module-conversation/ui';
import { salesRoutes } from '@saas/module-sales/ui';
import { projectRoutes } from '@saas/module-project/ui';
import { invoiceRoutes } from '@saas/module-invoice/ui';
import { taskPublicRoutes, taskRoutes } from '@saas/module-task/ui';
import { assetRoutes } from '@saas/module-asset/ui';
import { reportRoutes } from '@saas/module-report/ui';
import { calendarPublicRoutes, calendarRoutes } from '@saas/module-calendar/ui';
import { campaignPublicRoutes, campaignRoutes } from '@saas/module-campaign/ui';
import { telephonyRoutes } from '@saas/module-telephony/ui';
import { workflowRoutes } from '@saas/module-workflow/ui';
import { AppSkeleton } from './pages/AppSkeleton.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { RouteErrorPage } from './pages/RouteErrorPage.js';
import { SettingsHomePage } from './pages/SettingsHomePage.js';
import { WorkHomePage } from './pages/WorkHomePage.js';
import { AdminHomePage } from './pages/AdminHomePage.js';
import { ModuleGate, PublicOnly, RequireAuth } from './shell/guards.js';
import { Shell } from './shell/Shell.js';
import { ChatShell } from './shell/ChatShell.js';
import { InvoiceShell } from './shell/InvoiceShell.js';
import { ManualShell } from './shell/ManualShell.js';
import { TaskShell } from './shell/TaskShell.js';

/**
 * ルートの集約（03 第 I 部 §2: モジュールの ui/ はルート単位で遅延ロード）。
 *   /login /signup            … 公開（ログイン済みなら / へ）
 *   /invite/:token            … 招待の受諾。ログイン状態を問わない（別アカウントでログイン中でも開けてログアウトを促す）
 *   それ以外                   … 認証必須 + シェル。モジュールのルートは機能フラグ（ModuleGate）で 403 に
 */
const appRoutes: RouteObject[] = [
  { path: '/work', Component: WorkHomePage },
  { path: '/admin', Component: AdminHomePage },
  { path: '/settings', Component: SettingsHomePage },
  { path: '*', Component: NotFoundPage },
];

export const router = createBrowserRouter([
  {
    id: 'root',
    element: <Outlet />,
    errorElement: <RouteErrorPage />,
    HydrateFallback: AppSkeleton,
    children: [
      ...coreOpenRoutes,
      // 安否確認の回答（署名付きトークン・ログイン不要・03 §8）。本体の認証が落ちていても答えられる導線
      ...safetyPublicRoutes,
      // 共有リンク（社外・ログイン不要）。URL は ${APP_URL}/s/<token>（storage）
      ...storagePublicRoutes,
      ...surveyPublicRoutes,
      // 問い合わせの公開フォーム（サイト訪問者・ログイン不要・SFA §5.1）。URL は ${APP_URL}/f/<publicId>（crm）
      ...crmPublicRoutes,
      // 日程調整の公開リンク（見込み客・取引先の相手・ログイン不要・SFA §15.5 段階 4）。URL は ${APP_URL}/b/<token>（calendar）
      ...calendarPublicRoutes,
      // SFA 段階 5 §9.6: 一斉メールの配信停止ページ（/u/:token。ログイン不要）
      ...campaignPublicRoutes,
      ...meetingIngestPublicRoutes,
      ...taskPublicRoutes,
      ...contractPublicRoutes,
      { element: <PublicOnly />, children: corePublicRoutes },
      {
        element: <RequireAuth />,
        children: [
          { element: <ModuleGate module="chat" />, children: chatStandaloneRoutes },
          { element: <InvoiceShell />, children: [{ element: <ModuleGate module="invoice" />, children: invoiceRoutes }] },
          {
            element: <ManualShell />,
            children: [{ element: <ModuleGate module="wiki" />, children: wikiRoutes }],
          },
          {
            element: <ChatShell />,
            children: [{ element: <ModuleGate module="chat" />, children: chatRoutes }],
          },
          {
            element: <TaskShell />,
            children: [{ element: <ModuleGate module="task" />, children: taskRoutes }],
          },
          {
            element: <Shell />,
            children: [
              ...coreRoutes,
              { element: <ModuleGate module="workflow" />, children: workflowRoutes },
              { element: <ModuleGate module="expense" />, children: expenseRoutes },
              { element: <ModuleGate module="crm" />, children: crmRoutes },
              { element: <ModuleGate module="survey" />, children: surveyRoutes },
              { element: <ModuleGate module="contract" />, children: contractRoutes },
              { element: <ModuleGate module="safety" />, children: safetyRoutes },
              { element: <ModuleGate module="conversation" />, children: conversationRoutes },
              { element: <ModuleGate module="sales" />, children: salesRoutes },
              { element: <ModuleGate module="project" />, children: projectRoutes },
              { element: <ModuleGate module="asset" />, children: assetRoutes },
              { element: <ModuleGate module="report" />, children: reportRoutes },
              { element: <ModuleGate module="calendar" />, children: calendarRoutes },
              // SFA 段階 5: 一斉メール（§9.6）と電話連携（§9.4）
              { element: <ModuleGate module="campaign" />, children: campaignRoutes },
              { element: <ModuleGate module="telephony" />, children: telephonyRoutes },
              { element: <ModuleGate module="knowledge" />, children: knowledgeRoutes },
              { element: <ModuleGate module="relationship" />, children: relationshipRoutes },
              { element: <ModuleGate module="company-profile" />, children: companyProfileRoutes },
              { element: <ModuleGate module="meeting-ingest" />, children: meetingIngestRoutes },
              { element: <ModuleGate module="storage" />, children: storageRoutes },
              ...appRoutes,
            ],
          },
        ],
      },
    ],
  },
]);
